import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Zap, Radio } from "lucide-react";
import { Button } from "../ui/button";
import { api } from "@/lib/api";

/**
 * Dynadot webhook panel: shown at the bottom of the Dynadot Settings section.
 *
 * Renders:
 *  - The public inbound URL (only when DYNADOT_WEBHOOK_SECRET is set)
 *  - A "Test outbound webhook" button (fires a synthetic event to DYNADOT_WEBHOOK_URL)
 *  - The most recent inbound events (last 5), live-updated via SSE
 *    with a safety-net poll every 5s so we still see rows even when the
 *    ingress buffers text/event-stream.
 */
export default function DynadotWebhookPanel({ settings, reload }) {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [queueCounts, setQueueCounts] = useState({ pending: 0, delivered: 0, dead_letter: 0 });
  const [deadRows, setDeadRows] = useState([]);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [retrying, setRetrying] = useState({});
  const [liveStatus, setLiveStatus] = useState("connecting"); // connecting | live | polling
  const [flashId, setFlashId] = useState(null);
  const seenIdsRef = useRef(new Set());
  const flashTimerRef = useRef(null);

  const inboundSet = !!settings?.DYNADOT_WEBHOOK_SECRET?.set;
  const outboundSet = !!settings?.DYNADOT_WEBHOOK_URL?.set;
  const secretMasked = settings?.DYNADOT_WEBHOOK_SECRET?.masked || "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // We deliberately show a placeholder for the token so users know how the URL
  // looks; they'll copy the real one from the app they configure Dynadot with.
  const inboundUrl = inboundSet
    ? `${origin}/api/webhooks/dynadot/{DYNADOT_WEBHOOK_SECRET}`
    : "";

  /** Merge new events into state, prepending fresh ones and flashing the newest. */
  const mergeEvents = (incoming) => {
    if (!incoming || incoming.length === 0) return;
    let newest = null;
    setEvents((prev) => {
      const map = new Map(prev.map((e) => [e.id, e]));
      for (const e of incoming) {
        if (!e?.id) continue;
        if (!map.has(e.id)) {
          if (!seenIdsRef.current.has(e.id)) {
            newest = newest || e;   // first unseen one this batch → flash it
            seenIdsRef.current.add(e.id);
          }
        }
        map.set(e.id, e);
      }
      const merged = [...map.values()].sort((a, b) =>
        (b.received_at || "").localeCompare(a.received_at || "")
      );
      return merged.slice(0, 5);
    });
    if (newest) {
      setFlashId(newest.id);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlashId(null), 2500);
    }
  };

  const loadEvents = async () => {
    try {
      const { data } = await api.get("/dynadot/events?limit=5");
      const items = data.items || [];
      // Seed the dedupe set on first load so we don't flash pre-existing rows.
      if (seenIdsRef.current.size === 0) {
        items.forEach((e) => e?.id && seenIdsRef.current.add(e.id));
        setEvents(items);
      } else {
        mergeEvents(items);
      }
      setTotal(data.total || 0);
    } catch { /* silent */ }
    try {
      const { data } = await api.get("/dynadot/queue?limit=5");
      setQueueCounts(data.counts || { pending: 0, delivered: 0, dead_letter: 0 });
      const dead = await api.get("/dynadot/queue?status=dead_letter&limit=5");
      setDeadRows(dead.data.items || []);
    } catch { /* silent */ }
  };

  // Initial load + SSE subscription + safety-net polling. Same-origin so the
  // HttpOnly cookie auto-flows to EventSource.
  useEffect(() => {
    loadEvents();
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const streamUrl = `${backend}/api/dynadot/events/stream`;
    let es;
    try {
      es = new EventSource(streamUrl, { withCredentials: true });
      es.addEventListener("hello", () => setLiveStatus("live"));
      es.addEventListener("event", (msg) => {
        try {
          const row = JSON.parse(msg.data);
          mergeEvents([row]);
          setTotal((t) => t + 1);
        } catch { /* ignore malformed frame */ }
      });
      es.onerror = () => {
        // SSE dropped or blocked (buffered by proxy). Polling below still runs.
        setLiveStatus("polling");
      };
    } catch {
      setLiveStatus("polling");
    }
    // Safety-net poll — 5s cadence. Cheap: single query on a small collection.
    const timer = setInterval(loadEvents, 5000);
    return () => {
      if (es) es.close();
      clearInterval(timer);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const retryRow = async (rowId) => {
    setRetrying((r) => ({ ...r, [rowId]: true }));
    try {
      const { data } = await api.post(`/dynadot/queue/${rowId}/retry`);
      if (data.row?.status === "delivered") toast.success("Delivered");
      else toast.warning(`Still ${data.row?.status || "pending"}`);
      await loadEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying((r) => ({ ...r, [rowId]: false }));
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/dynadot/webhook-test");
      setTestResult(data);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Test failed";
      setTestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
      reload && reload();
    }
  };

  const copy = (txt) => {
    try {
      navigator.clipboard.writeText(txt);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div
      className="mt-4 pt-4 border-t border-dashed border-[#eaecf0] space-y-4"
      data-testid="dynadot-webhook-panel"
    >
      <div className="text-[10px] uppercase tracking-[0.24em] font-mono text-[#666]">
        Webhook wiring
      </div>

      {/* Inbound URL */}
      <div className="rounded-sm border border-[#eaecf0] p-3 bg-[#FAFAFA]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Radio className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="text-xs font-medium">Inbound webhook URL</span>
          </div>
          <span
            className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${
              inboundSet
                ? "bg-[#ECFDF5] text-[#047857]"
                : "bg-[#FFFBEB] text-[#B45309]"
            }`}
            data-testid="inbound-status"
          >
            {inboundSet ? "READY" : "SET WEBHOOK SECRET FIRST"}
          </span>
        </div>
        {inboundSet ? (
          <>
            <div className="flex items-center gap-2">
              <code
                data-testid="inbound-url"
                className="flex-1 text-[11px] font-mono bg-white border border-[#eaecf0] rounded-sm px-2 py-1.5 truncate"
              >
                {inboundUrl}
              </code>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-sm h-8"
                onClick={() => copy(inboundUrl)}
                data-testid="copy-inbound-url-btn"
              >
                <Copy className="w-3 h-3" strokeWidth={1.5} />
              </Button>
            </div>
            <p className="text-[10px] font-mono text-[#666] mt-2 leading-relaxed">
              Replace <code>{"{DYNADOT_WEBHOOK_SECRET}"}</code> with your real secret
              (currently: <code>{secretMasked || "unset"}</code>) when configuring
              Zapier / Dynadot / your automation. Any POSTed JSON is normalized
              to <code>{"{event, data}"}</code> and stored in <code>dynadot_events</code>.
              Domain-lifecycle events also appear in the Inbox.
            </p>
          </>
        ) : (
          <p className="text-[10px] font-mono text-[#666] leading-relaxed">
            Save a value into <code>DYNADOT_WEBHOOK_SECRET</code> above (any random string
            works) — the inbound URL will unlock. It's used as a path token so any HTTP
            client can push to us without needing to sign requests.
          </p>
        )}
      </div>

      {/* Outbound test */}
      <div className="rounded-sm border border-[#eaecf0] p-3 bg-[#FAFAFA]">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Zap className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="text-xs font-medium">Outbound webhook</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm h-8"
            onClick={runTest}
            disabled={testing || !outboundSet}
            data-testid="test-outbound-webhook-btn"
          >
            {testing ? "Firing…" : "Fire test event"}
          </Button>
        </div>
        <p className="text-[10px] font-mono text-[#666] leading-relaxed">
          When set, we POST a signed JSON envelope
          (<code>X-CasperDomains-Signature: sha256=…</code> using
          <code> DYNADOT_WEBHOOK_SECRET</code>) on <code>snipe.won</code> and
          <code> listing.sold</code> events to <code>DYNADOT_WEBHOOK_URL</code>.
        </p>
        {testResult && (
          <div
            data-testid="test-outbound-result"
            className={`mt-2 text-[11px] font-mono ${
              testResult.ok ? "text-[#047857]" : "text-[#BE123C]"
            }`}
          >
            {testResult.message}
          </div>
        )}
      </div>

      {/* Retry queue summary */}
      <div className="rounded-sm border border-[#eaecf0] p-3 bg-[#FAFAFA]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium">Retry queue</span>
          <div
            className="flex items-center gap-3 text-[10px] font-mono"
            data-testid="queue-counts"
          >
            <span className="text-[#047857]">✓ {queueCounts.delivered}</span>
            <span className="text-[#B45309]">⟳ {queueCounts.pending}</span>
            <span className="text-[#BE123C]">✗ {queueCounts.dead_letter}</span>
          </div>
        </div>
        <p className="text-[10px] font-mono text-[#666] leading-relaxed">
          Failed outbound events auto-retry with exponential backoff
          (30s → 2m → 10m → 1h → 6h → 24h). After 6 attempts they land in
          <em> dead letter</em> and appear below for manual retry once your
          receiver is back.
        </p>
        {deadRows.length > 0 && (
          <ul
            className="mt-2 divide-y divide-[#F0F0F0] border-t border-[#F0F0F0] pt-2"
            data-testid="dead-letter-list"
          >
            {deadRows.map((r) => (
              <li
                key={r.id}
                className="py-2 flex items-center justify-between gap-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono truncate">{r.event}</div>
                  <div className="text-[10px] font-mono text-[#BE123C] truncate">
                    {r.last_error || `HTTP ${r.last_status_code}`}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-7 text-[10px]"
                  disabled={!!retrying[r.id]}
                  onClick={() => retryRow(r.id)}
                  data-testid={`retry-row-${r.id}`}
                >
                  {retrying[r.id] ? "Retrying…" : "Retry"}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Recent inbound events */}
      <div className="rounded-sm border border-[#eaecf0]">
        <div className="px-3 py-2 border-b border-[#eaecf0] bg-[#FAFAFA] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium">Recent inbound events</span>
            <span
              data-testid="live-status"
              className={`inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded-sm ${
                liveStatus === "live"
                  ? "bg-[#ECFDF5] text-[#047857]"
                  : liveStatus === "polling"
                  ? "bg-[#EFF6FF] text-[#2563EB]"
                  : "bg-[#F5F5F5] text-[#666]"
              }`}
              title={
                liveStatus === "live"
                  ? "Server-Sent Events connected"
                  : liveStatus === "polling"
                  ? "SSE unavailable · polling every 5s"
                  : "connecting…"
              }
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  liveStatus === "live"
                    ? "bg-[#047857] animate-pulse"
                    : liveStatus === "polling"
                    ? "bg-[#2563EB]"
                    : "bg-[#A1A1AA]"
                }`}
              />
              {liveStatus === "live" ? "LIVE" : liveStatus === "polling" ? "POLLING" : "…"}
            </span>
          </div>
          <span className="text-[10px] font-mono text-[#666]" data-testid="events-total">
            {total} total
          </span>
        </div>
        {events.length === 0 ? (
          <div className="p-3 text-[11px] font-mono text-[#A1A1AA] italic">
            No inbound events yet.
          </div>
        ) : (
          <ul className="divide-y divide-[#F0F0F0]">
            {events.map((e) => (
              <li
                key={e.id}
                className={`px-3 py-2 flex items-center justify-between gap-3 transition-colors duration-500 ${
                  flashId === e.id ? "bg-[#F0FDF4]" : ""
                }`}
                data-testid={`event-row-${e.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono truncate">{e.event}</div>
                  <div className="text-[10px] font-mono text-[#666] truncate">
                    {e.data?.domain || e.data?.name || JSON.stringify(e.data || {}).slice(0, 60)}
                  </div>
                </div>
                <div className="text-[10px] font-mono text-[#A1A1AA] whitespace-nowrap">
                  {e.received_at ? new Date(e.received_at).toLocaleString() : ""}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
